// src/context/DesignerApplicationContext.tsx
import React, { createContext, useContext, useState, ReactNode, useCallback, useEffect, useRef } from "react";
import { useDeleteDesignerAccountMutation } from "../hooks/useDeleteDesignerAccountMutation";
import { useDeleteDesignerApplicationMutation } from "../hooks/useDeleteDesignerApplicationMutation";
import { useCreateMembershipApplicationMutation } from "../hooks/useCreateMembershipApplicationMutation";
import { useUpdateMembershipApplicationMutation } from "../hooks/useUpdateMembershipApplicationMutation";
import { useGetDesignerApplicationQuery } from "../hooks/useGetDesignerApplicationQuery";
import { useUserContext } from "../hooks/userContext";
import { DesignerApplication, DesignerSubscriptionType, MutationCreateDesignerApplicationArgs } from "../__generated__/types";
import { getMembershipTierFromPlanId, getSinglePaymentItemFromId, hasApplicationChanged } from "../utils";
import { removeTypeNames } from "../hooks/designer-application-context/utils";

/** Public type used by consumers via the shim */
export type DesignerApplicationContextState = {
  applications: any[]; // keep for compatibility
  loading: boolean;
  application: any;

  approveDesignerApplication: (
    id: string
  ) => Promise<true | { error: string }>;

  rejectDesignerApplication: (
    id: string,
    reason?: string
  ) => Promise<true | { error: string }>;

  deleteDesignerAccount: (
    id: string
  ) => Promise<true | { error: string }>;

  deleteDesignerApplication: (
    id: string
  ) => Promise<true | { error: string }>;

  /** Callers merge partial application updates */
  updateApplicationData: (value: Partial<any>) => void;

  /** Immediate save for critical data like references */
  saveApplicationDataImmediately: (value: Partial<any>) => Promise<void>;

  /** Explicit reset */
  clearApplicationData: () => void;

  /** Optional flags/ops retained for compatibility */
  hasApplicationDataLoaded?: boolean;
  refreshApplicationData?: () => void;
  createApplication?: (args: any) => Promise<any>;
  saveDesignerApplication?: (app?: Partial<any>) => Promise<any>;
};

const DesignerApplicationContext =
  createContext<DesignerApplicationContextState | null>(null);

export const DesignerApplicationProvider = ({
  children,
}: {
  children: ReactNode;
}) => {
  const [applications, setApplications] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [applicationDraft, setApplicationDraft] = useState<any>({});
  const [applicationId, setApplicationId] = useState<string | null>(null);
  const autoSaveTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const hasFetchedApplicationData = useRef(false);
  const previousUserIdRef = useRef<string | null>(null);
  
  const { user } = useUserContext();
  const { deleteDesignerAccount: deleteDesignerAccountMutation } = useDeleteDesignerAccountMutation();
  const { deleteDesignerApplication: deleteDesignerApplicationMutation } = useDeleteDesignerApplicationMutation();
  
  const {
    createApplication: createApplicationMutation,
  } = useCreateMembershipApplicationMutation();

  const {
    updateApplication,
    loading: updateApplicationLoading,
  } = useUpdateMembershipApplicationMutation();

  const {
    getDesignerApplication: getDesignerApplicationQuery,
    data: designerApplicationDataFromServer,
    loading: designerApplicationLoading,
    error: designerApplicationError,
  } = useGetDesignerApplicationQuery(user?.id ?? "");
  


  const getDesignerApplication = useCallback(() => {
    if (user?.id) {
      getDesignerApplicationQuery();
    }
  }, [user?.id, getDesignerApplicationQuery]);

  // Reset local application state when authenticated user changes.
  useEffect(() => {
    const currentUserId = user?.id ?? null;
    if (previousUserIdRef.current === currentUserId) {
      return;
    }

    previousUserIdRef.current = currentUserId;
    hasFetchedApplicationData.current = false;
    setApplicationDraft({});
    setApplicationId(null);
  }, [user?.id]);

  // Load existing application data when user logs in
  useEffect(() => {
    
    if (user?.id && designerApplicationDataFromServer) {
      const cleanedData = removeTypeNames(designerApplicationDataFromServer, user.id);
      setApplicationDraft(cleanedData);
      setApplicationId(designerApplicationDataFromServer._id);
    } else if (!user) {
      // Reset when user logs out
      hasFetchedApplicationData.current = false;
      setApplicationDraft({});
      setApplicationId(null);
    }
  }, [user?.id, designerApplicationDataFromServer]);

  // Fetch application data when user is available
  useEffect(() => {
    
    if (
      !hasFetchedApplicationData.current &&
      user?.id && 
      !designerApplicationDataFromServer && 
      !designerApplicationLoading
    ) {
      hasFetchedApplicationData.current = true;
      getDesignerApplication();
    }
  }, [user?.id, designerApplicationDataFromServer, designerApplicationLoading, getDesignerApplication]);

  const createApplication = useCallback(
    async (args: MutationCreateDesignerApplicationArgs) => {
      try {
        setLoading(true);
        if (!designerApplicationDataFromServer) {
          const result = await createApplicationMutation(args);
          if (result) {
            setApplicationId(result);
            return result;
          }
        }
        return applicationId;
      } catch (error) {
        throw error;
      } finally {
        setLoading(false);
      }
    },
    [designerApplicationDataFromServer, createApplicationMutation, applicationId]
  );

  const saveDesignerApplication = useCallback(
    async (application: Partial<DesignerApplication> = {}) => {
      try {
        
        setLoading(true);
        
        const membershipPlanId = (application as any)?.membershipPlanId ?? (applicationDraft as any)?.membershipPlanId ?? "";
        const membershipTier = getMembershipTierFromPlanId(membershipPlanId);
        
        
        const update = {
          ...applicationDraft,
          ...application,
          membershipTier: membershipTier as DesignerSubscriptionType,
          singlePaymentItems:
            ((applicationDraft?.singlePaymentItems as any) ?? [])?.map(
              (item: string) => getSinglePaymentItemFromId(item)
            ) ?? [],
        };
        delete (update as any).__typename;

        const hasChanged = hasApplicationChanged(
          removeTypeNames(designerApplicationDataFromServer, user?.id ?? ""),
          removeTypeNames(update as DesignerApplication, user?.id ?? "")
        );


        delete (update as any).designerId;
        delete (update as any).membershipPlanId; // Remove membershipPlanId as it's not accepted by the schema
        if (designerApplicationDataFromServer) {
          (update as any)._id = (designerApplicationDataFromServer as any)._id;
        }

        if (hasChanged) {
          // Clean the update data by removing __typename fields before saving
          const cleanedUpdate = removeTypeNames(update as DesignerApplication, user?.id ?? "");
          
          // If no application exists yet, try to create one first
          if (!applicationId && !designerApplicationDataFromServer) {
            try {
              const newAppId = await createApplication({
                designerId: user?.id!,
                experience: cleanedUpdate.experience || [],
              });
              if (newAppId) {
                setApplicationId(newAppId);
                (cleanedUpdate as any)._id = newAppId;
              }
            } catch (error: any) {
              if (error.message?.includes("Application already exists")) {
                // Skip the update since we can't create or update without an application ID
                return;
              } else {
                throw error;
              }
            }
          }
          
          // Now update the application
          if (cleanedUpdate._id) {
            // Normalize enum fields: convert empty strings to null/undefined
            const normalizedUpdate: any = { ...cleanedUpdate };
            if (normalizedUpdate.experience && Array.isArray(normalizedUpdate.experience)) {
              normalizedUpdate.experience = normalizedUpdate.experience.map((exp: any) => {
                const normalizedExp: any = { ...exp };
                if (normalizedExp.employmentType === '') normalizedExp.employmentType = null;
                if (normalizedExp.firmSize === '') normalizedExp.firmSize = null;
                return normalizedExp;
              });
            }
            if (normalizedUpdate.references && Array.isArray(normalizedUpdate.references)) {
              normalizedUpdate.references = normalizedUpdate.references.map((ref: any) => {
                const normalizedRef: any = { ...ref };
                if (normalizedRef.type === '') normalizedRef.type = null;
                if (normalizedRef.ProjectType === '') normalizedRef.ProjectType = null;
                return normalizedRef;
              });
            }
            if (normalizedUpdate.businessGoals?.projectCapacity) {
              if (normalizedUpdate.businessGoals.projectCapacity.largeScale === '') {
                normalizedUpdate.businessGoals.projectCapacity.largeScale = null;
              }
              if (normalizedUpdate.businessGoals.projectCapacity.smallScale === '') {
                normalizedUpdate.businessGoals.projectCapacity.smallScale = null;
              }
            }
            
            const result = await updateApplication({
              application: {
                ...normalizedUpdate,
                _id: normalizedUpdate._id!,
              },
            });
            if (result) {
              setApplicationDraft(cleanedUpdate);
              return result;
            }
          }
        } else {
        }
        return applicationDraft;
      } catch (error) {
        throw error;
      } finally {
        setLoading(false);
      }
    },
    [
      applicationDraft,
      updateApplication,
      designerApplicationDataFromServer,
      user?.id,
      applicationId,
      createApplication,
    ]
  );

  const refreshApplicationData = useCallback(async () => {
    if (user?.id) {
      hasFetchedApplicationData.current = false;
      await getDesignerApplication();
    }
  }, [user?.id, getDesignerApplication]);

  const approveDesignerApplication = async (
    id: string
  ): Promise<true | { error: string }> => {
    try {
      setLoading(true);
      // TODO: call API/mutation to approve
      setApplications((prev) => prev.filter((a) => a?.id !== id));
      return true;
    } catch (e: any) {
      return { error: e?.message ?? "Unknown error approving application" };
    } finally {
      setLoading(false);
    }
  };

  const rejectDesignerApplication = async (
    id: string,
    _reason?: string
  ): Promise<true | { error: string }> => {
    try {
      setLoading(true);
      // TODO: call API/mutation to reject (optionally send reason)
      setApplications((prev) => prev.filter((a) => a?.id !== id));
      return true;
    } catch (e: any) {
      return { error: e?.message ?? "Unknown error rejecting application" };
    } finally {
      setLoading(false);
    }
  };

  const deleteDesignerAccount = async (
    id: string
  ): Promise<true | { error: string }> => {
    try {
      setLoading(true);
      const result = await deleteDesignerAccountMutation(id);
      
      if (result) {
        setApplications((prev) => prev.filter((a) => a?.id !== id));
        return true;
      } else {
        return { error: "Failed to delete designer account" };
      }
    } catch (e: any) {
      return { error: e?.message ?? "Unknown error deleting designer account" };
    } finally {
      setLoading(false);
    }
  };

  const deleteDesignerApplication = async (
    id: string
  ): Promise<true | { error: string }> => {
    try {
      setLoading(true);
      const result = await deleteDesignerApplicationMutation(id);
      
      if (result) {
        setApplications((prev) => prev.filter((a) => a?.id !== id));
        return true;
      } else {
        return { error: "Failed to delete designer application" };
      }
    } catch (e: any) {
      return { error: e?.message ?? "Unknown error deleting designer application" };
    } finally {
      setLoading(false);
    }
  };

  /** Merge partial updates with auto-save */
  const updateApplicationData = useCallback((value: Partial<any>) => {
    
    const newDraft = { ...applicationDraft, ...value };
    setApplicationDraft(newDraft);
    
    // Clear existing timeout
    if (autoSaveTimeoutRef.current) {
      clearTimeout(autoSaveTimeoutRef.current);
    }
    
    // Set new timeout for auto-save (5 seconds delay to reduce frequency)
    autoSaveTimeoutRef.current = setTimeout(async () => {
      try {
        if (user?.id && applicationId) {
          await saveDesignerApplication(newDraft);
        } else {
          console.log("DesignerApplicationContext: Auto-save skipped", {
            userId: user?.id,
            applicationId
          });
        }
      } catch (error) {
        console.error("Auto-save failed:", error);
        // Don't show error to user for auto-save failures
      }
    }, 5000);
  }, [applicationDraft, applicationId, user?.id, saveDesignerApplication]);

  /** Immediate save for critical data like references */
  const saveApplicationDataImmediately = useCallback(async (value: Partial<any>) => {
    
    const newDraft = { ...applicationDraft, ...value };
    setApplicationDraft(newDraft);
    
    // Clear any pending auto-save since we're saving immediately
    if (autoSaveTimeoutRef.current) {
      clearTimeout(autoSaveTimeoutRef.current);
    }
    
    try {
      if (user?.id && applicationId) {
        await saveDesignerApplication(newDraft);
        
        // Refresh application data from server to ensure UI is in sync
        await refreshApplicationData();
      } else {
        console.log("DesignerApplicationContext: Immediate save skipped", {
          userId: user?.id,
          applicationId
        });
      }
    } catch (error) {
      throw error; // Re-throw for immediate save failures so UI can handle them
    }
  }, [applicationDraft, applicationId, user?.id, saveDesignerApplication, refreshApplicationData]);

  /** Explicit reset for logout/clear flows */
  const clearApplicationData = () => {
    setApplicationDraft({});
    setApplicationId(null);
    // Clear any pending auto-save
    if (autoSaveTimeoutRef.current) {
      clearTimeout(autoSaveTimeoutRef.current);
    }
  };

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (autoSaveTimeoutRef.current) {
        clearTimeout(autoSaveTimeoutRef.current);
      }
    };
  }, []);

  // Use the most recent data - prioritize draft if it has newer changes
  const currentApplication = applicationDraft && Object.keys(applicationDraft).length > 0 
    ? applicationDraft 
    : designerApplicationDataFromServer;
  

  return (
    <DesignerApplicationContext.Provider
      value={{
        applications,
        loading: loading || updateApplicationLoading || designerApplicationLoading,
        application: currentApplication,
        approveDesignerApplication,
        rejectDesignerApplication,
        deleteDesignerAccount,
        deleteDesignerApplication,
        updateApplicationData,
        saveApplicationDataImmediately,
        clearApplicationData,
        hasApplicationDataLoaded: !!designerApplicationDataFromServer,
        refreshApplicationData,
        createApplication,
        saveDesignerApplication,
      }}
    >
      {children}
    </DesignerApplicationContext.Provider>
  );
};

export const useDesignerApplicationContext =
  (): DesignerApplicationContextState => {
    const ctx = useContext(DesignerApplicationContext);
    if (!ctx) {
      throw new Error(
        "useDesignerApplicationContext must be used within a DesignerApplicationProvider"
      );
    }
    return ctx;
  };
